/* eslint-disable no-console */
import * as path from "path";

import boxen from "boxen";
import { readJson } from "fs-extra";
import { compare, parse } from "semver";
import { exec } from "shelljs";

import { runAsyncAction } from "../../Common/TypingUtils";

import { ELKLogger } from "./Commons/Logging/ELKLogger";
import { defaultRegistry } from "./Commons/Defaults";

export function selfUpdateWithCallback(callback: () => void): void {
    runAsyncAction(async () => {
        await selfUpdate();
        callback();
    });
}

async function selfUpdate(): Promise<void> {
    if (process.argv.includes("--development-mode")) {
        return;
    }
    try {
        process.stdout.write("Checking for updates... ");
        const versionToUpdate = await getCandyToolUpdateVersion();
        if (versionToUpdate != undefined) {
            const message = "candy обновляется. Пожалуйста, не закрывайте консоль. Это может занять некоторое время.";
            console.log(boxen(message, { borderStyle: "double", borderColor: "green", padding: 2, margin: 2 }));
            const { npm_config_registry: _, ...env } = process.env;
            exec(
                (process.platform === "linux" ? "unbuffer " : "") + `npm i -g @kontur.candy/tools@${versionToUpdate}`,
                {
                    env: {
                        ...env,
                        NPM_CONFIG_REGISTRY: defaultRegistry,
                    },
                }
            );
            console.log(`Updated to ${versionToUpdate}`);
        } else {
            console.log("All up to date.");
        }
    } catch (e) {
        if (e instanceof Error) {
            ELKLogger.error("Failed to check for updates", { stackTrace: e.stack });
        }
        console.warn("**********************************************");
        console.warn("* Failed to check for updates                *");
        console.warn("* Please report to developers                *");
        console.warn("**********************************************");
    }
}

export async function getCurrentVersion(): Promise<string> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (await readJson(path.join(__dirname, "..", "..", "..", "package.json"))).version;
}

async function getCandyToolUpdateVersion(): Promise<string | undefined> {
    const currentVersion: string = await getCurrentVersion();
    let output;
    try {
        output = await new Promise<string>((resolve, reject) => {
            exec(
                (process.platform === "linux" ? "unbuffer " : "") +
                    `npm view @kontur.candy/tools versions --json --no-progress`,
                { async: true, silent: true, env: { ...process.env, NPM_CONFIG_REGISTRY: defaultRegistry } },
                (code, stdout, stderr) => {
                    if (code !== 0) {
                        reject(new Error(stderr));
                    } else {
                        resolve(stdout);
                    }
                }
            );
        });
    } catch (e) {
        if (e instanceof Error) {
            ELKLogger.error("Failed to get versions", { stackTrace: e.stack });
        }
        console.warn("**********************************************");
        console.warn("* Failed to check for updates                *");
        console.warn("* Please report to developers                *");
        console.warn("**********************************************");
        return undefined;
    }
    const availableVersions: string[] = JSON.parse(output);
    return getMajorOrMinorIncremented(currentVersion, availableVersions);
}

export function getMajorOrMinorIncremented(currentVersion: string, availableVersions: string[]): string | undefined {
    const current = parse(currentVersion);
    if (current == undefined) {
        throw new Error(`Current version '${currentVersion}' is not a valid version`);
    }

    if (current.prerelease.length > 0) {
        console.log("The prerelease version is installed, we use it without updating");
        return undefined;
    }

    const result = availableVersions
        .map(x => parse(x, undefined))
        .filter(isNotNullOrUndefined)
        .sort(compare)
        .reverse()
        .find(x => {
            if (x.prerelease.length > 0) {
                return false;
            }
            return x.major > current.major || (x.major === current.major && x.minor > current.minor);
        });
    return result != undefined ? result.format() : undefined;
}

function isNotNullOrUndefined<T>(input: T | undefined | null): input is T {
    return input != undefined;
}
